import { useState, useEffect, useContext } from 'react';
import {
    Modal, ModalDialog, ModalClose, DialogTitle, DialogContent, Stack, FormControl,
    FormLabel, Input, Button, Select, Option, Typography, Box, IconButton, Divider,
    Chip, Alert, Accordion, AccordionGroup, AccordionSummary, AccordionDetails, Radio, RadioGroup
} from '@mui/joy';
import { LayoutContext } from '../../context';
import { APIGetContentTree, APICreateClinicalCase, APIUpdateClinicalCase, APIUploadQuestionImage } from '../../api';
import RichTextEditor from '../ui/RichTextEditor';
import { Plus, Trash, CaretDown } from "@phosphor-icons/react";

export default function ClinicalCaseModal({ open, onClose, editingCase }) {
    const { token } = useContext(LayoutContext);
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [uploadingImage, setUploadingImage] = useState(false);

    const [formData, setFormData] = useState({
        case_text: '',
        difficulty: 'medium',
        focus_tag: 'diagnosis',
        category_ids: [],
        questions: []
    });

    useEffect(() => {
        if (open && token) {
            fetchCategories();
        }
    }, [open, token]);

    useEffect(() => {
        if (open) {
            if (editingCase) {
                // Edit mode
                setFormData({
                    case_text: editingCase.case_text || '',
                    difficulty: editingCase.difficulty || 'medium',
                    focus_tag: editingCase.focus_tag || 'diagnosis',
                    category_ids: editingCase.categories ? editingCase.categories.map(cat => cat.id) : [],
                    questions: editingCase.questions || []
                });
            } else {
                // Create mode
                setFormData({
                    case_text: '',
                    difficulty: 'medium',
                    focus_tag: 'diagnosis',
                    category_ids: [],
                    questions: [{
                        text: '',
                        explanation: '',
                        answers: [
                            { text: '', is_correct: false },
                            { text: '', is_correct: false },
                            { text: '', is_correct: false },
                            { text: '', is_correct: false },
                            { text: '', is_correct: false }
                        ]
                    }]
                });
            }
        }
    }, [open, editingCase]);

    const fetchCategories = async () => {
        try {
            const response = await APIGetContentTree(token);
            if (response.success) {
                const flatCategories = flattenCategories(response.tree);
                setCategories(flatCategories);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const flattenCategories = (tree, prefix = '') => {
        let result = [];
        tree.forEach(cat => {
            result.push({
                id: cat.id,
                name: prefix + cat.name,
                fullPath: prefix + cat.name
            });
            if (cat.children && cat.children.length > 0) {
                result = [...result, ...flattenCategories(cat.children, prefix + cat.name + ' → ')];
            }
        });
        return result;
    };

    const handleImageUpload = async (file) => {
        setUploadingImage(true);
        try {
            const response = await APIUploadQuestionImage(token, file);
            if (response.success) {
                return response.url;
            } else {
                throw new Error(response.error);
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Ошибка при загрузке изображения');
            return null;
        } finally {
            setUploadingImage(false);
        }
    };

    const handleAddQuestion = () => {
        setFormData(prev => ({
            ...prev,
            questions: [
                ...prev.questions,
                {
                    text: '',
                    explanation: '',
                    answers: [
                        { text: '', is_correct: false },
                        { text: '', is_correct: false },
                        { text: '', is_correct: false },
                        { text: '', is_correct: false },
                        { text: '', is_correct: false }
                    ]
                }
            ]
        }));
    };

    const handleRemoveQuestion = (qIndex) => {
        setFormData(prev => ({
            ...prev,
            questions: prev.questions.filter((_, i) => i !== qIndex)
        }));
    };

    const handleQuestionChange = (qIndex, field, value) => {
        setFormData(prev => ({
            ...prev,
            questions: prev.questions.map((q, i) =>
                i === qIndex ? { ...q, [field]: value } : q
            )
        }));
    };

    const handleAnswerChange = (qIndex, aIndex, field, value) => {
        setFormData(prev => ({
            ...prev,
            questions: prev.questions.map((q, qi) => {
                if (qi !== qIndex) return q;
                return {
                    ...q,
                    answers: q.answers.map((a, ai) => {
                        if (ai !== aIndex) {
                            // If setting is_correct to true for one answer, uncheck others
                            if (field === 'is_correct' && value === true) {
                                return { ...a, is_correct: false };
                            }
                            return a;
                        }
                        return { ...a, [field]: value };
                    })
                };
            })
        }));
    };

    const handleSave = async () => {
        // Validation
        if (!formData.case_text.trim()) {
            alert('Введите описание клинического случая');
            return;
        }

        if (formData.category_ids.length === 0) {
            alert('Выберите хотя бы одну категорию');
            return;
        }

        if (formData.questions.length === 0) {
            alert('Добавьте хотя бы один вопрос');
            return;
        }

        // Validate each question
        for (let i = 0; i < formData.questions.length; i++) {
            const q = formData.questions[i];
            if (!q.text.trim()) {
                alert(`Вопрос ${i + 1}: введите текст вопроса`);
                return;
            }

            const filledAnswers = q.answers.filter(a => a.text.trim());
            if (filledAnswers.length < 2) {
                alert(`Вопрос ${i + 1}: введите минимум 2 варианта ответа`);
                return;
            }

            const correctAnswers = q.answers.filter(a => a.is_correct);
            if (correctAnswers.length === 0) {
                alert(`Вопрос ${i + 1}: укажите правильный ответ`);
                return;
            }

            if (correctAnswers.length > 1) {
                alert(`Вопрос ${i + 1}: можно выбрать только один правильный ответ`);
                return;
            }
        }

        setLoading(true);
        try {
            // Prepare questions data
            const questionsData = formData.questions.map(q => ({
                text: q.text,
                explanation: q.explanation,
                answers: q.answers.filter(a => a.text.trim()).map(a => ({
                    text: a.text,
                    is_correct: a.is_correct
                }))
            }));

            const payload = {
                case_text: formData.case_text,
                difficulty: formData.difficulty,
                focus_tag: formData.focus_tag,
                category_ids: formData.category_ids,
                questions: questionsData
            };

            let response;
            if (editingCase) {
                response = await APIUpdateClinicalCase(token, editingCase.id, payload);
            } else {
                response = await APICreateClinicalCase(token, payload);
            }

            if (response.success) {
                onClose(true); // Signal to refresh
            } else {
                alert('Ошибка: ' + response.error);
            }
        } catch (error) {
            console.error('Save error:', error);
            alert('Ошибка при сохранении');
        } finally {
            setLoading(false);
        }
    };

    const getAnswerLabel = (index) => {
        return String.fromCharCode(65 + index); // A, B, C, D, E
    };

    return (
        <Modal open={open} onClose={() => onClose(false)}>
            <ModalDialog
                sx={{
                    maxWidth: 900,
                    maxHeight: '90vh',
                    overflow: 'auto',
                    width: '100%'
                }}
            >
                <ModalClose />
                <DialogTitle>
                    {editingCase ? 'Редактировать клиническую задачу' : 'Создать клиническую задачу'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Alert color="primary" size="sm">
                            Клинический случай должен содержать 4-7 предложений о пациенте. Вопросы должны быть в формате USMLE Step 1 (5 вариантов ответа, только один правильный).
                        </Alert>

                        {/* Case text */}
                        <FormControl required>
                            <FormLabel>Описание клинического случая</FormLabel>
                            <RichTextEditor
                                value={formData.case_text}
                                onChange={(value) => setFormData(prev => ({ ...prev, case_text: value }))}
                                placeholder="Введите описание клинического случая (4-7 предложений)..."
                                onImageUpload={handleImageUpload}
                                uploadingImage={uploadingImage}
                            />
                        </FormControl>

                        {/* Difficulty */}
                        <FormControl required>
                            <FormLabel>Сложность</FormLabel>
                            <Select
                                value={formData.difficulty}
                                onChange={(e, value) => setFormData(prev => ({ ...prev, difficulty: value }))}
                            >
                                <Option value="easy">Легкая</Option>
                                <Option value="medium">Средняя</Option>
                                <Option value="hard">Сложная</Option>
                            </Select>
                        </FormControl>

                        {/* Focus tag */}
                        <FormControl required>
                            <FormLabel>Фокус задачи</FormLabel>
                            <Select
                                value={formData.focus_tag}
                                onChange={(e, value) => setFormData(prev => ({ ...prev, focus_tag: value }))}
                            >
                                <Option value="diagnosis">Диагноз</Option>
                                <Option value="pathogenesis">Этиология/Патогенез</Option>
                                <Option value="treatment">Лечение</Option>
                            </Select>
                        </FormControl>

                        {/* Categories */}
                        <FormControl required>
                            <FormLabel>Категории</FormLabel>
                            <Select
                                multiple
                                value={formData.category_ids}
                                onChange={(e, value) => setFormData(prev => ({ ...prev, category_ids: value }))}
                                placeholder="Выберите категории"
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {selected.map((option) => {
                                            const category = categories.find(c => c.id === option.value);
                                            return (
                                                <Chip key={option.value} size="sm" variant="soft">
                                                    {category?.name || option.value}
                                                </Chip>
                                            );
                                        })}
                                    </Box>
                                )}
                            >
                                {categories.map((cat) => (
                                    <Option key={cat.id} value={cat.id}>
                                        {cat.fullPath}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider sx={{ my: 2 }} />

                        {/* Questions */}
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography level="title-md">
                                    Вопросы ({formData.questions.length})
                                </Typography>
                                <Button
                                    size="sm"
                                    startDecorator={<Plus size={18} />}
                                    onClick={handleAddQuestion}
                                >
                                    Добавить вопрос
                                </Button>
                            </Box>

                            <AccordionGroup>
                                {formData.questions.map((question, qIndex) => (
                                    <Accordion key={qIndex}>
                                        <AccordionSummary indicator={<CaretDown size={18} />}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                <Typography level="title-sm">
                                                    Вопрос {qIndex + 1}
                                                </Typography>
                                                <IconButton
                                                    size="sm"
                                                    variant="outlined"
                                                    color="danger"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveQuestion(qIndex);
                                                    }}
                                                >
                                                    <Trash size={18} />
                                                </IconButton>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Stack spacing={2}>
                                                {/* Question text */}
                                                <FormControl required>
                                                    <FormLabel>Текст вопроса</FormLabel>
                                                    <Input
                                                        value={question.text}
                                                        onChange={(e) => handleQuestionChange(qIndex, 'text', e.target.value)}
                                                        placeholder="Введите текст вопроса..."
                                                    />
                                                </FormControl>

                                                {/* Answers */}
                                                <FormControl required>
                                                    <FormLabel>Варианты ответа (выберите один правильный)</FormLabel>
                                                    <RadioGroup
                                                        value={question.answers.findIndex(a => a.is_correct)}
                                                        onChange={(e) => {
                                                            const correctIndex = parseInt(e.target.value);
                                                            question.answers.forEach((_, aIndex) => {
                                                                handleAnswerChange(qIndex, aIndex, 'is_correct', aIndex === correctIndex);
                                                            });
                                                        }}
                                                    >
                                                        <Stack spacing={1}>
                                                            {question.answers.map((answer, aIndex) => (
                                                                <Box key={aIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Radio
                                                                        value={aIndex}
                                                                        label={getAnswerLabel(aIndex)}
                                                                        size="sm"
                                                                    />
                                                                    <Input
                                                                        value={answer.text}
                                                                        onChange={(e) => handleAnswerChange(qIndex, aIndex, 'text', e.target.value)}
                                                                        placeholder={`Вариант ${getAnswerLabel(aIndex)}...`}
                                                                        sx={{ flex: 1 }}
                                                                    />
                                                                </Box>
                                                            ))}
                                                        </Stack>
                                                    </RadioGroup>
                                                </FormControl>

                                                {/* Explanation */}
                                                <FormControl>
                                                    <FormLabel>Объяснение</FormLabel>
                                                    <RichTextEditor
                                                        value={question.explanation}
                                                        onChange={(value) => handleQuestionChange(qIndex, 'explanation', value)}
                                                        placeholder="Введите объяснение правильного ответа..."
                                                        onImageUpload={handleImageUpload}
                                                        uploadingImage={uploadingImage}
                                                    />
                                                </FormControl>
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </AccordionGroup>
                        </Box>

                        {/* Save button */}
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2 }}>
                            <Button variant="plain" onClick={() => onClose(false)}>
                                Отмена
                            </Button>
                            <Button onClick={handleSave} loading={loading}>
                                {editingCase ? 'Сохранить' : 'Создать'}
                            </Button>
                        </Box>
                    </Stack>
                </DialogContent>
            </ModalDialog>
        </Modal>
    );
}
